import { useMemo, useRef, useState, useEffect } from 'react';
import { BaseAssetProps } from '../../assetComponents/types';
import { AssetWrapper } from '../../assetComponents/assetWrapper/AssetWrapper';
import { TagItem } from 'src/features/sites/components/tag-item';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';
import { useGetTagByIdQuery } from 'src/hooks/reactQueryApiHooks/useGetTagByIdQuery';
import { useAuthentication } from 'src/features/authentication/context';
import { Box, Typography } from '@mui/material';
import { ComponentType } from 'src/types/SCADA/componentsTypes';
import { DASHBOARD_TEST_LABELS } from 'src/features/customDashboard/constants';

type Props = BaseAssetProps;

export const LiveTagLabel = ({ selected, width, height, data, id }: Props) => {
  const {
    customerId: { value: customerId },
  } = useAuthentication();

  const tagId = data?.tagId;
  const gatewayId = data?.gatewayId;
  const siteId = data?.siteId;

  const { tag } = useGetTagByIdQuery({
    customerId: customerId!,
    siteId: siteId,
    inputId: tagId,
    gatewayId: gatewayId,
  });

  const { messages } = useWebSockets();
  const liveDataComplete = useMemo(() => {
    if (!messages || !gatewayId) return null;

    if (Array.isArray(messages)) {
      const matchedMessage = messages.find((msg: any) => msg?.data?.sourceId === gatewayId);
      if (!matchedMessage) return null;

      return { tags: matchedMessage.data.data };
    }

    if (messages?.data?.sourceId === gatewayId) {
      return { tags: messages.data.data };
    }

    return null;
  }, [messages, gatewayId]);

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [measuredSize, setMeasuredSize] = useState<{ width: number; height: number }>({
    width: width ?? 50,
    height: height ?? 20,
  });

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      if (!contentRef.current) return;
      setMeasuredSize({
        width: contentRef.current.scrollWidth,
        height: contentRef.current.scrollHeight,
      });
    };

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <AssetWrapper
      selected={selected}
      width={measuredSize.width}
      height={measuredSize.height}
      id={id}
      type={ComponentType.LiveTagLabel}
    >
      <Box ref={contentRef} data-sm={DASHBOARD_TEST_LABELS.LIVE_DATA_LABEL}>
        {tag ? (
          <TagItem
            tag={tag}
            isMobile={false}
            testId={`tag-item-${tag?.id}`}
            liveDataComplete={liveDataComplete}
            isSCADA
            isDisplayName={data?.isDisplayName ?? true}
          />
        ) : (
          <Typography>Live data label</Typography>
        )}
      </Box>
    </AssetWrapper>
  );
};
